"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "@/components/waves/drops/dropRankStyles";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation } from "@/components/waves/drops/drop.types";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import WaveDropActions from "@/components/waves/drops/WaveDropActions";
import MemeWinnerHeader from "./MemeWinnerHeader";
import MemeWinnerDescription from "./MemeWinnerDescription";
import MemeWinnerArtistInfo from "./MemeWinnerArtistInfo";
import MemeDropTraits from "./MemeDropTraits";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { useDropContext } from "@/components/waves/drops/DropContext";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
interface MemeWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showReplyAndQuote: boolean;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly showInteractions?: boolean | undefined;
}
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
export default function MemeWinnerDrop(t0) {
  const $ = _c(49);
  const {
    drop,
    showReplyAndQuote,
    onReply,
    showInteractions: t1
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isMobile = useIsMobileDevice();
  const {
    location
  } = useDropContext();
  const title = drop.metadata.find(_temp)?.data_value ?? "Artwork Title";
  const description = drop.metadata.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
  let t2;
  if ($[0] !== drop.parts) {
    t2 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const artworkMedia = t2;
  let t3;
  if ($[2] !== drop || $[3] !== onReply) {
    t3 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[2] = drop;
    $[3] = onReply;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleOnReply = t3;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  let t4;
  if ($[5] !== drop) {
    t4 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><MemeWinnerArtistInfo drop={drop} /></div>;
    $[5] = drop;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== title) {
    t5 = <MemeWinnerHeader title={title} />;
    $[7] = title;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== description) {
    t6 = <MemeWinnerDescription description={description} />;
    $[9] = description;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t5}{t6}</div></div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== drop) {
    t8 = <WaveWinnerIdentity drop={drop} variant="full" className="tw-px-4 tw-pb-4" />;
    $[14] = drop;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== artworkMedia) {
    t9 = artworkMedia && <div className="tw-mx-0.5 tw-flex tw-h-96 tw-justify-center tw-bg-iron-950"><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[16] = artworkMedia;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== drop) {
    t10 = <div className="tw-mt-4 tw-hidden tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 lg:tw-block"><MemeDropTraits drop={drop} /></div>;
    $[18] = drop;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t4 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t11 = <>{t4}{t7}{t8}{t9}{t10}</>;
    $[20] = t10;
    $[21] = t4;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const content = t11;
  const t12 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-group tw-relative`;
  let t13;
  if ($[26] !== effectiveRank) {
    t13 = getRankStaticBorderClass(effectiveRank);
    $[26] = effectiveRank;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== effectiveRank) {
    t14 = getRankHoverClass(effectiveRank);
    $[28] = effectiveRank;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = `tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t13} tw-transition-all tw-duration-200 tw-ease-out ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"} ${t14}`;
  let t16;
  if ($[30] !== content || $[31] !== drop || $[32] !== handleOnReply || $[33] !== showInteractions || $[34] !== showReplyAndQuote) {
    t16 = showInteractions ? <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{content}</DropMobileMenuHandler> : content;
    $[30] = content;
    $[31] = drop;
    $[32] = handleOnReply;
    $[33] = showInteractions;
    $[34] = showReplyAndQuote;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== drop || $[37] !== handleOnReply || $[38] !== isMobile || $[39] !== showInteractions || $[40] !== showReplyAndQuote) {
    t17 = !isMobile && showInteractions && showReplyAndQuote && <div className="tw-absolute tw-right-4 tw-top-2"><WaveDropActions drop={drop} activePartIndex={0} onReply={handleOnReply} /></div>;
    $[36] = drop;
    $[37] = handleOnReply;
    $[38] = isMobile;
    $[39] = showInteractions;
    $[40] = showReplyAndQuote;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t15 || $[43] !== t16 || $[44] !== t17) {
    t18 = <div className={t15}>{t16}{t17}</div>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t12 || $[47] !== t18) {
    t19 = <div className="tw-mb-3 tw-w-full"><div className={t12}>{t18}</div></div>;
    $[46] = t12;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}